import { useState } from 'react'
import type { Activity } from '@/types/crm'
import { formatDate } from '@/shared/util/dates'
import { NoteComposer } from '@/shared/notes/NoteComposer'
import '@/shared/notes/NotesField.css'

/** How much of the log is worth showing before anybody asks for the rest. */
const PREVIEW = 3

/**
 * The log and the box that writes to it, side by side, near the top of the panel.
 *
 * ## Why it moved, 8 Sep 2026
 *
 * This was the last disclosure on the panel, closed by default — so recording
 * what just happened on a call meant scrolling past every card and opening a
 * section, and reading what happened last time meant the same. Both are the
 * most frequent acts on a lead. It sits under the company fields now, open.
 *
 * Split rather than stacked, because the two halves are different jobs: writing
 * does not need the history in view, and reading it does not need a textarea in
 * the way. Stacked, the composer pushed the last note below the fold on exactly
 * the leads with enough history to be worth reading.
 *
 * Only the last {@link PREVIEW} entries show. That is the answer to "where did
 * we leave this" — the rest is an archive, and an archive that is always open
 * is the reason the section was folded away in the first place.
 *
 * ## What it keeps from AVV-2026-003
 *
 * The composer is still `NoteComposer`, shared with `NotesField` on PRO
 * companies, so Enter is a newline and ⌘↵ saves — a note about a conversation
 * runs to paragraphs, and the old single-line input filed half of one
 * permanently.
 *
 * Hand-written notes are still marked out of the system churn around them
 * ("Moved to Won", "Owner changed to …"), and the filter still shows them
 * alone. A note rendered identically to a dozen automatic lines is a note
 * nobody finds.
 */
export function ActivityPanel({
  activity,
  onAddNote,
}: {
  activity: Activity[]
  onAddNote: (text: string) => Promise<void>
}) {
  const [notesOnly, setNotesOnly] = useState(false)
  const [expanded, setExpanded] = useState(false)

  const noteCount = activity.filter((a) => a.source === 'user').length
  const matching = notesOnly ? activity.filter((a) => a.source === 'user') : activity
  const shown = expanded ? matching : matching.slice(0, PREVIEW)
  const hidden = matching.length - shown.length

  return (
    <section className="card actpanel">
      <div className="actpanel__compose">
        <div className="card-label">Add note</div>
        <NoteComposer onSave={onAddNote} rows={4} label="Add note" />
      </div>

      <div className="actpanel__log">
        <div className="activity__head">
          <div className="card-label activity__label">
            Activity
            {noteCount > 0 && <span className="notes__count"> · {noteCount} notes</span>}
          </div>
          {/*
            Only offered when it would do something. A filter on a log that has
            nothing but system entries is a control that appears to fail.
          */}
          {noteCount > 0 && activity.length > noteCount && (
            <label className="activity__filter">
              <input
                type="checkbox"
                checked={notesOnly}
                onChange={(e) => setNotesOnly(e.target.checked)}
              />
              Notes only
            </label>
          )}
        </div>

        <div className={`activity__list${expanded ? ' activity__list--all' : ''}`}>
          {shown.map((a) => (
            <div
              className={`activity__row${a.source === 'user' ? ' activity__row--note' : ''}`}
              key={a.id}
            >
              <div className="activity__date">{formatDate(a.at)}</div>
              <div>
                <span className="activity__author">{a.author}</span>
                <span className="muted"> — {a.text}</span>
              </div>
            </div>
          ))}
          {activity.length === 0 && (
            <div className="empty-note">
              Nothing logged against this lead yet. Notes, emails and stage changes appear here as
              they happen.
            </div>
          )}
          {activity.length > 0 && matching.length === 0 && (
            <div className="empty-note">
              No hand-written notes on this lead yet — everything logged here so far was recorded
              automatically. Clear the filter to see it.
            </div>
          )}
        </div>

        {(hidden > 0 || expanded) && (
          <button className="linkbtn actpanel__more" onClick={() => setExpanded((v) => !v)}>
            {expanded ? 'Show less' : `Show all ${matching.length}`}
          </button>
        )}
      </div>
    </section>
  )
}
